import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import { useCart } from "@context/CartContext";
import Modal from "@/components/common/Modal";
import InlineEmailOtpVerification from "@/components/common/InlineEmailOtpVerification";
import { validatePhoneNumber } from "@/lib/validation/phoneValidator";

export default function Enrollment() {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionRequiresAdvisorFollowUp, setSubmissionRequiresAdvisorFollowUp] =
    useState(false);
  const [error, setError] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [formData, setFormData] = useState({
    program: "",
    format: "",
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91",
    mobileNumber: "",
    company: "",
    role: "",
    goals: "",
  });
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  const { addToCart } = useCart();

  // Handle URL query parameters
  useEffect(() => {
    if (router.isReady) {
      const { program, format } = router.query;

      if (program || format) {
        setFormData((prev) => ({
          ...prev,
          program: program || prev.program,
          format: format || prev.format,
        }));

        if (program && format) {
          setStep(3);
        } else if (program) {
          setStep(2);
        }
      }
    }
  }, [router.isReady, router.query]);

  // Fetch programs for selection
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch("/api/programs");
        const result = await res.json();
        if (result.success) {
          setPrograms(result.data);
        }
      } catch (err) {
        console.error("Error fetching programs:", err);
      } finally {
        setLoadingPrograms(false);
      }
    };
    fetchPrograms();
  }, []);

  const activeProgram = programs.find(
    (p) => (p.slug || p.id) === formData.program,
  );
  const formats = activeProgram?.formats || [];
  const selectedFormat = formats.find((f) => f.id === formData.format);
  const isAdvisorEnrollment = router.query.intent === "advisor";
  const requiresAdvisorFollowUp = Boolean(
    isAdvisorEnrollment ||
    activeProgram?.isContactForPrice ||
    activeProgram?.isLeadOnly ||
    (selectedFormat && !(selectedFormat.price > 0)),
  );

  // Check if format selection step should be shown
  const showFormatStep = activeProgram &&
    activeProgram.formats &&
    activeProgram.formats.length > 0 &&
    !activeProgram.isContactForPrice &&
    !activeProgram.isLeadOnly;

  const nextStep = () => {
    setError(null);

    if (step === 3) {
      const phoneValidation = validatePhoneNumber(
        formData.countryCode,
        formData.mobileNumber,
      );

      if (!phoneValidation.valid) {
        setError(phoneValidation.error);
        return;
      }
    }

    // Skip format step for lead-only/corporate programs
    if (step === 1 && !showFormatStep) {
      setStep(3);
    } else {
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    setError(null);
    // Skip back from step 3 to step 1 for lead-only/corporate programs
    if (step === 3 && !showFormatStep) {
      setStep(1);
    } else {
      setStep(step - 1);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [step, submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    if (verifiedEmail !== formData.email.trim().toLowerCase()) {
      setError("Please verify your email with OTP before submitting.");
      setSubmitting(false);
      return;
    }

    try {
      const phoneValidation = validatePhoneNumber(
        formData.countryCode,
        formData.mobileNumber,
      );

      if (!phoneValidation.valid) {
        setError(phoneValidation.error);
        setSubmitting(false);
        return;
      }

      const activeProgram = programs.find(
        (p) => (p.slug || p.id) === formData.program,
      );

      if (!activeProgram) {
        setError("Please select a program");
        setSubmitting(false);
        return;
      }

      const formatItem = activeProgram.formats?.find((f) => f.id === formData.format);
      const shouldRequireAdvisorFollowUp = Boolean(
        isAdvisorEnrollment ||
        activeProgram.isContactForPrice ||
        activeProgram.isLeadOnly ||
        (formatItem && !(formatItem.price > 0)),
      );

      const response = await fetch("/api/enroll/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          programId: activeProgram._id,
          programName: activeProgram.title,
          formatName: formatItem
            ? `${formatItem.name} (${formatItem.subTitle || "Standard"})`
            : formData.format,
          ...formData,
          countryCode: phoneValidation.countryCode,
          mobileNumber: phoneValidation.mobileNumber,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmissionRequiresAdvisorFollowUp(shouldRequireAdvisorFollowUp);
        setSubmitted(true);
        setSubmitting(false);

        // Add to cart if eligible
        if (
          activeProgram &&
          !shouldRequireAdvisorFollowUp &&
          formatItem
        ) {
          addToCart({
            programId: activeProgram._id,
            formatId: formatItem.id,
            title: activeProgram.slug === 'full-leadership' ? formatItem.name : activeProgram.title,
            price: formatItem.price || 0,
            featuredImg: activeProgram.featuredImg,
          });
        }
      } else {
        setError(
          data.suggestion
            ? `${data.message} ${data.suggestion}`
            : (data.message || "Something went wrong. Please try again."),
        );
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Enrollment error:", err);
      setError("Failed to submit. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Enrollment | L.I.S.T Learning Pathways</title>
        <meta
          name="description"
          content="Enroll in our transformative leadership programs designed for to modern professional."
        />
      </Head>

      <main className="min-h-screen bg-gray-50">
        <Modal
          isOpen={submitted && submissionRequiresAdvisorFollowUp}
          onClose={() => router.push("/")}
        >
          <div className="max-w-lg mx-auto text-center py-6 px-2">
            <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-brand-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thank you for your interest in L.I.S.T.
            </h2>
            <p className="text-gray-600 mb-8">
              An advisor will get in touch with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/programs"
                className="inline-block px-8 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-lg hover:shadow-brand-primary/30 transition-all active:scale-95"
              >
                Explore Programs
              </Link>
              <Link
                href="/"
                className="inline-block px-8 py-3 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-all active:scale-95"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </Modal>
        {submitted ? (
          <div className="max-w-3xl mx-auto px-4 py-12">
            {/* Success Message */}
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {submissionRequiresAdvisorFollowUp
                  ? "Enrollment Submitted Successfully!"
                  : "Enrollment Submitted Successfully!"}
              </h2>
              <p className="text-gray-600 mb-8">
                {submissionRequiresAdvisorFollowUp
                  ? "Thank you for your interest in L.I.S.T. An advisor will get in touch with you."
                  : "Thank you for enrolling. Your advisor will contact you shortly to finalize your enrollment. If you're ready to secure your spot immediately, you can proceed to payment now."}
              </p>
              {!submissionRequiresAdvisorFollowUp && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    href="/checkout"
                    className="inline-block px-8 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-lg hover:shadow-brand-primary/30 transition-all active:scale-95"
                  >
                    Proceed to Payment
                  </Link>
                  <Link
                    href="/"
                    className="inline-block px-8 py-3 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-all active:scale-95"
                  >
                    Back to Home
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-12">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-black text-brand-dark mb-3">
                Begin Your Leadership Journey
              </h1>
              <p className="text-gray-600 text-lg">
                Complete the form below to enroll in our programs. Your advisor
                will contact you to finalize your enrollment.
              </p>
            </div>

            {/* Progress Indicators */}
            <div className="flex justify-center gap-3 mb-12">
              {showFormatStep
                ? [1, 2, 3, 4].map((s) => (
                  <div
                    key={s}
                    className={`h-2 rounded-full transition-all duration-300 ${step >= s ? "w-16 bg-brand-primary" : "w-2 bg-gray-300"}`}
                  />
                ))
                : [1, 2, 3].map((s, idx) => {
                  // Map visual step 2 to actual step 3 for lead-only programs
                  const actualStep = idx + 1 >= 2 ? idx + 2 : idx + 1;
                  return (
                    <div
                      key={s}
                      className={`h-2 rounded-full transition-all duration-300 ${step >= actualStep ? "w-16 bg-brand-primary" : "w-2 bg-gray-300"}`}
                    />
                  );
                })
              }
            </div>

            {/* Main Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
              {loadingPrograms ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-500 font-medium">
                    Loading programs...
                  </p>
                </div>
              ) : programs.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    No Programs Available
                  </h3>
                  <p className="text-gray-600">
                    Check back soon for new programs or contact us for custom
                    training solutions.
                  </p>
                  <Link
                    href="/contact"
                    className="inline-block px-8 py-3 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-dark transition-all"
                  >
                    Contact Us
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Error Alert */}
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg animate-in fade-in shrink-0">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-bold text-red-800">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Step 1: Program Selection */}
                  {step === 1 && (
                    <div className="animate-in slide-in-from-right-4 duration-300">
                      <h2 className="text-2xl font-bold text-brand-dark mb-6">
                        Select Your Program
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Choose a program that aligns with your career goals and
                        learning style.
                      </p>

                      <div className="space-y-4">
                        {programs.map((program) => (
                          <button
                            type="button"
                            key={program._id}
                            onClick={() => {
                              setError(null);
                              setFormData({
                                ...formData,
                                program: formData.program === program.slug ? "" : program.slug,
                              });
                            }}
                            className={`w-full p-6 border-2 rounded-xl text-left transition-all ${formData.program === (program.slug || program.id)
                              ? "border-brand-primary bg-brand-primary/5"
                              : "border-gray-200 hover:border-brand-primary hover:bg-gray-50"
                              }`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div>
                                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    {program.title}
                                  </h3>
                                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                                    {program.description}
                                  </p>
                                </div>
                                {program.badge && (
                                  <span className="inline-block px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider rounded-full">
                                    {program.badge}
                                  </span>
                                )}
                              </div>
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${formData.program ===
                                  (program.slug || program.id)
                                  ? "bg-brand-primary text-white"
                                  : "bg-gray-200 text-gray-600"
                                  }`}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Format Selection */}
                  {step === 2 && showFormatStep && (
                    <div className="animate-in slide-in-from-right-4 duration-300">
                      <h2 className="text-2xl font-bold text-brand-dark mb-6">
                        Choose Your Format
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Select the format that best fits your schedule and
                        learning preferences.
                      </p>

                      <div className="grid gap-4">
                        {activeProgram.formats.map((format) => (
                          <button
                            type="button"
                            key={format.id}
                            onClick={() => {
                              setError(null);
                              setFormData({ ...formData, format: formData.format === format.id ? "" : format.id });
                            }}
                            className={`w-full p-6 border-2 rounded-xl text-left transition-all ${formData.format === format.id
                              ? "border-brand-primary bg-brand-primary/5"
                              : "border-gray-200 hover:border-brand-primary hover:bg-gray-50"
                              }`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                  {format.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-3">
                                  {format.subTitle === 'Offline' ? 'On-site' : (format.subTitle || "Standard format")}
                                </p>
                              </div>
                              <div className="text-right">
                                <span className="text-xl font-bold text-brand-primary">
                                  {format.price > 0 ? `₹${format.price.toLocaleString()}` : "Price upon request"}
                                </span>
                                {format.price > 0 && (
                                  <span className="block mt-1 text-xs font-medium text-gray-500">
                                    Price Inclusive of 18% GST
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {(step === 1 || step === 2) && (
                    <div className="flex justify-between mt-8">
                      <button
                        type="button"
                        onClick={prevStep}
                        disabled={step <= 1}
                        className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={
                          step === 1
                            ? !formData.program
                            : !formData.format
                        }
                        className="px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-xl hover:shadow-brand-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue
                      </button>
                    </div>
                  )}

                  {/* Step 3: Personal Details */}
                  {step === 3 && (
                    <div className="animate-in slide-in-from-right-4 duration-300">
                      <h2 className="text-2xl font-bold text-brand-dark mb-6">
                        Your Details
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Please provide your contact information and any
                        additional details.
                      </p>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                firstName: e.target.value,
                              })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                lastName: e.target.value,
                              })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                email: e.target.value,
                              })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                            required
                          />
                          <div className="mt-3">
                            <InlineEmailOtpVerification
                              email={formData.email}
                              verifiedEmail={verifiedEmail}
                              onVerifiedEmailChange={setVerifiedEmail}
                              context="enrollment"
                              mode="manual"
                            />
                          </div>
                        </div>
                        <div className="grid md:grid-cols-[140px,1fr] gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Country Code
                            </label>
                            <input
                              type="text"
                              value={formData.countryCode}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  countryCode: e.target.value,
                                })
                              }
                              placeholder="+91"
                              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Mobile Number
                            </label>
                            <input
                              type="tel"
                              value={formData.mobileNumber}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  mobileNumber: e.target.value,
                                })
                              }
                              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                              inputMode="numeric"
                              placeholder="9876543210"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company (Optional)
                          </label>
                          <input
                            type="text"
                            value={formData.company}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                company: e.target.value,
                              })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Role (Optional)
                          </label>
                          <input
                            type="text"
                            value={formData.role}
                            onChange={(e) =>
                              setFormData({ ...formData, role: e.target.value })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Your Goals (Optional)
                          </label>
                          <textarea
                            rows="4"
                            value={formData.goals}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                goals: e.target.value,
                              })
                            }
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all resize-none"
                            placeholder="Share your leadership goals..."
                          />
                        </div>
                      </div>

                      <div className="flex justify-between mt-8">
                        <button
                          type="button"
                          onClick={prevStep}
                          disabled={step <= 1}
                          className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={nextStep}
                          disabled={
                            !formData.firstName ||
                            !formData.lastName ||
                            !formData.email ||
                            verifiedEmail !== formData.email.trim().toLowerCase() ||
                            !formData.countryCode ||
                            !formData.mobileNumber
                          }
                          className="px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-xl hover:shadow-brand-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Summary & Submit */}
                  {step === 4 && activeProgram && (
                    <div className="animate-in slide-in-from-right-4 duration-300">
                      <h2 className="text-2xl font-bold text-brand-dark mb-6">
                        Review & Submit
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Please review your enrollment details before submitting.
                      </p>

                      {/* Summary Card */}
                      <div className="bg-brand-primary/5 rounded-2xl p-6 mb-6 border border-brand-primary/20">
                        <h3 className="text-xl font-bold text-brand-dark mb-4">
                          Selected Program
                        </h3>

                        <div className="space-y-3 mb-6">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">
                              Program
                            </span>
                            <span className="text-lg font-bold text-brand-dark">
                              {activeProgram.title}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">
                              Format
                            </span>
                            <span className="text-lg font-bold text-brand-dark">
                              {formData.format
                                ? formats.find((f) => f.id === formData.format)
                                  ?.name || formData.format
                                : "N/A"}
                            </span>
                          </div>
                          {formats.find((f) => f.id === formData.format)
                            ?.price > 0 && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Price Inclusive of 18% GST
                                </span>
                                <span className="text-lg font-bold text-brand-primary">
                                  ₹
                                  {formats
                                    .find((f) => f.id === formData.format)
                                    ?.price?.toLocaleString()}
                                </span>
                              </div>
                            )}
                          {formats.find((f) => f.id === formData.format) && !(formats.find((f) => f.id === formData.format)?.price > 0) && (
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Price Inclusive of 18% GST
                              </span>
                              <span className="text-lg font-bold text-brand-primary">
                                Price upon request
                              </span>
                            </div>
                          )}
                        </div>

                        {activeProgram.badge && (
                          <div className="mt-4 pt-4 border-t border-brand-primary/10">
                            <span className="inline-block px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider rounded-full">
                              {activeProgram.badge}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Add to cart Button - Only for eligible programs */}
                      {activeProgram &&
                        !requiresAdvisorFollowUp &&
                        selectedFormat && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  addToCart({
                                    programId: activeProgram._id,
                                    formatId: selectedFormat.id,
                                    title: activeProgram.title,
                                    price: selectedFormat.price || 0,
                                    featuredImg: activeProgram.featuredImg,
                                  });
                                }}
                                className="flex-1 px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-xl hover:shadow-brand-primary transition-all text-sm"
                              >
                                Add to Cart
                              </button>
                            </div>
                          </div>
                        )}

                      {!submitting ? (
                        <div className="flex justify-between mt-8">
                          <button
                            type="button"
                            onClick={prevStep}
                            disabled={step <= 1}
                            className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            className="px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:shadow-xl hover:shadow-brand-primary/30 transition-all"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span>Submit Enrollment</span>
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-center mt-8">
                          <div className="px-6 py-3 bg-brand-primary text-white font-bold rounded-lg inline-flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <p className="text-center text-xs text-gray-300 mt-12">
                    By submitting, you agree to our{" "}
                    <Link
                      href="/terms"
                      className="underline text-brand-primary font-semibold hover:text-brand-dark"
                    >
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="/privacy"
                      className="underline text-brand-primary font-semibold hover:text-brand-dark"
                    >
                      Privacy Policy
                    </Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </main >
    </>
  );
}
